<template>
  <div>
    <!-- Table -->
    <div
      ref="tableContainer"
      class="overflow-auto"
      @scroll="setBoxShadow"
    >
      <table :class="[
        'table',
        {
          'table-layout-auto': tableLayout === 'auto' || sticky,
          'table-sticky': sticky,
          'table-sticky--left': sticky === 'both' || sticky === 'left',
          'table-sticky--right': sticky === 'both' || sticky === 'right',
        }
      ]">
        <thead ref="tableHead">
          <tr>
            <th v-if="checkedCount > 1" :colspan="columns.length">
              <div class="table-action-list d-inline-flex font-weight-normal align-items-center">
                <m-form-checkbox
                  ref="checkAllTrigger"
                  :checked="checkedCount === values.length"
                  :indeterminate="checkedCount !== 0 && checkedCount < values.length"
                  class="mr-1"
                  @change="updateAllCheckedTable"
                />
                <slot
                  name="bulkAction"
                  :checked-count="checkedCount"
                  :checked-rows="checkedRows"
                >
                  <div class="d-inline-block">{{ checkedCount }} records has been selected.</div>
                  <button class="btn btn-sm btn-secondary ml-4">Reject All</button>
                  <button class="btn btn-sm btn-primary ml-3">Approve All</button>
                </slot>
              </div>
            </th>
            <template v-if="checkedCount <= 1">
              <th
                v-for="(column, idx) in columns"
                :ref="`tableHeaderCol-${idx}`"
                :key="`table-header-col-${idx}`"
                scope="col"
                :class="[
                  column.cssClass,
                  `th-${column.key}`,
                  `tc-${column.key}`,
                  {
                    sort: column.sortable,
                    'sort-up': column.sortable && sort === `${column.sortKey}-asc`,
                    'sort-down':
                      column.sortable && sort === `${column.sortKey}-desc`,
                    'border-bottom-0': !isLoading && isEmpty,
                    'box-shadow-table': isStickyRight && isStickyBoth && idx === columns.length - 1
                  },
                ]"
                :style="column.cssStyle"
                @click="column.sortable ? sortList(column) : null"
              >
                <div class="d-flex">
                  <m-form-checkbox
                    v-if="selectable && idx === 0"
                    ref="checkAllTrigger"
                    :checked="checkedCount === values.length"
                    :indeterminate="checkedCount !== 0 && checkedCount < values.length"
                    class="mr-1"
                    @change="updateAllCheckedTable"
                  />
                  <span>{{ column.label }}</span>
                  <m-tooltip
                    v-if="column.tooltip"
                    placement="top"
                    trigger="hover"
                    :target="`tooltip-table-${column.key}`"
                    :content="column.tooltip"
                    :class="{
                      'pl-3': column.sortable
                    }"
                  />
                  <m-icon
                    v-if="column.tooltip"
                    size="small"
                    variant="info"
                    :id="`tooltip-table-${column.key}`"
                  />
                </div>
              </th>
            </template>
          </tr>
        </thead>
        <tbody v-if="isLoading" ref="tableBody">
          <tr>
            <td :colspan="columns.length">
              <div class="py-5">
                <m-spinner spinner-type="lg" />
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!isLoading && !isEmpty" ref="tableBody">
          <tr v-for="(value, valueIndex) in values" :key="`table-body-row-${valueIndex}`">
            <td
              v-for="(column, columnIndex) in columns"
              :key="`table-body-row-${valueIndex}-${columnIndex}`"
              :class="[
                column.contentCssClass,
                `td-${column.key}`,
                {
                  'box-shadow-table': isStickyRight && isStickyBoth && columnIndex === columns.length - 1
                }
              ]"
              :style="column.contentCssStyle"
            >
              <div class="d-flex">
                <m-form-checkbox
                  v-if="selectable && columnIndex === 0"
                  :ref="`checkbox-${valueIndex}`"
                  :checked="value.checked"
                  class="mr-1"
                  @change="updateCheckedTable(valueIndex, $event)"
                />
                <slot
                  v-if="column.isCustom"
                  :name="column.key"
                  :column="column"
                  :contents="value.contents"
                  :index="columnIndex"
                >
                  {{ value.contents[column.key] }}
                </slot>
                <div v-else-if="column.isIncrement">
                  {{ increment + valueIndex }}
                </div>
                <div v-else>
                  {{ value.contents[column.key] }}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty State Slot -->
    <div v-if="!isLoading && isEmpty" class="py-5">
      <slot name="blankSlate" />
    </div>

    <!-- Pagination -->
    <m-pagination
      v-if="!isEmpty"
      :metas="metas"
      :disabled="isLoading"
      @update="updatePagination"
    />
  </div>
</template>

<script>
import MTooltip from '../Tooltip';
import MIcon from '../Icon';
import MPagination from '../Pagination';
import MSpinner from '../Spinner';
import { BFormCheckbox as MFormCheckbox } from 'bootstrap-vue';

export default {
  name: 'MTable',
  components: {
    MFormCheckbox,
    MIcon,
    MPagination,
    MSpinner,
    MTooltip,
  },
  props: {
    columns: {
      type: Array,
      default() {
        return [];
      },
    },
    values: {
      type: Array,
      default() {
        return [];
      },
    },
    increment: {
      type: Number,
      default: 1,
    },
    sort: {
      type: String,
      default: '',
    },
    metas: {
      type: Object,
      default() {
        return {
          current_page: 1,
          last_page: 1,
          per_page: 10,
          total: 1,
        };
      },
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    tableLayout: {
      type: String,
      default: 'auto',
    },
    sticky: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      checkedRows: {},
      checkedCount: 0,
    };
  },
  watch: {
    columns: {
      deep: true,
      handler() {
        this.initCheckedTable();
      },
    },
  },
  computed: {
    isEmpty() {
      return !this.values.length >= 1;
    },
    isStickyLeft() {
      return this.sticky && this.sticky === 'left';
    },
    isStickyRight() {
      return this.sticky && this.sticky === 'right';
    },
    isStickyBoth() {
      return this.sticky && this.sticky === 'both';
    },
  },
  mounted() {
    this.initCheckedTable();
  },
  methods: {
    setBoxShadow(e) {
      const lastNumber = `${this.columns.length - 1}`;
      const { scrollWidth } = e.target;
      const { clientWidth } = e.target;
      if (e.target.scrollLeft > 0) {
        if (this.isStickyLeft || this.isStickyBoth) {
          document.querySelectorAll('.td-0').forEach(el => el.classList.add('box-shadow-table'));
          document.querySelectorAll('.th-0').forEach(el => el.classList.add('box-shadow-table'));
        }
      } else if (this.isStickyLeft || this.isStickyBoth) {
        document.querySelectorAll('.td-0').forEach(el => el.classList.remove('box-shadow-table'));
        document.querySelectorAll('.th-0').forEach(el => el.classList.remove('box-shadow-table'));
      }
      if (e.target.scrollLeft < (scrollWidth - clientWidth)) {
        if (this.isStickyRight || this.isStickyBoth) {
          document.querySelectorAll(`.td-${lastNumber}`).forEach(el => el.classList.add('box-shadow-table'));
          document.querySelectorAll(`.th-${lastNumber}`).forEach(el => el.classList.add('box-shadow-table'));
        }
      } else if (this.isStickyRight || this.isStickyBoth) {
        document.querySelectorAll(`.td-${lastNumber}`).forEach(el => el.classList.remove('box-shadow-table'));
        document.querySelectorAll(`.th-${lastNumber}`).forEach(el => el.classList.remove('box-shadow-table'));
      }
    },
    sortList(column) {
      this.$emit('sort-update', column.sortKey);
    },
    updatePagination({ page, limit }) {
      this.$emit('pagination-update', { current_page: page, per_page: limit });
    },
    updateCheckedTable(key, value) {
      if (value) {
        this.checkedCount += 1;
      } else {
        this.checkedCount -= 1;
      }
      this.$set(this.checkedRows, key, value);
      this.$emit('checked', this.checkedRows);
    },
    updateAllCheckedTable(value) {
      this.values.forEach((el, index) => this.$set(this.checkedRows, index, value));
      this.checkedCount = 0;
      if (value) {
        this.checkedCount = this.values.length;
      }
      this.$emit('checked', this.checkedRows);
    },
    initCheckedTable() {
      this.checkedCount = 0;
      this.values.forEach((el, index) => {
        if (el.checked) {
          this.checkedCount += 1;
          this.$set(this.checkedRows, index, true);
        } else {
          this.$set(this.checkedRows, index, false);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$noir: #212121;
$slate: #777;
$dust: #b4b4b4;

.td-action,
.td-actions {
  .btn,
  .btn:hover,
  .btn:focus {
    outline: none !important;
    border: none !important;
    background: none !important;
    box-shadow: none !important;
  }
}

table {
  table-layout: fixed;
  .custom-control {
    margin-bottom: 0;
    min-height: 14px;
    padding-top: 1px;
  }
  th,
  td {
    width: calc(75% / 3);
    &.table-checkbox {
      padding-right: 0;
      width: 16px;
    }
  }

  th:last-child,
  td:last-child {
    width: 25%;
  }

  .sort {
    pointer-events: auto !important;
    cursor: pointer;

    &.disabled {
      pointer-events: none;

      span {
        color: $dust;
      }
    }

    span::before,
    span::after {
      margin-left: 6px;
    }

    &-up {
      span::before {
        border-color: transparent transparent $noir transparent !important;
      }

      span::after {
        border-color: $slate transparent transparent transparent !important;
      }
    }

    &-down {
      span::before {
        border-color: transparent transparent $slate transparent !important;
      }

      span::after {
        border-color: $noir transparent transparent transparent !important;
      }
    }
  }
}

.box-shadow-table {
  box-shadow: 0 4px 8px rgba(119, 119, 119, .1), 0 12px 20px rgba(119, 119, 119, .2);
}
</style>
